import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { AdHocSendTab } from "@/components/admin/email/AdHocSendTab";
import { LogTab, ScheduleTab, SuppressionTab } from "@/components/admin/email/EmailSequenceTabs";
import { useEmailTemplate, useEmailTemplates } from "@/hooks/admin/emailTemplates";
import { AdminLayout } from "@/layouts/AdminLayout";
import type { EmailTemplateListItem } from "@/types";

/** "Sends once" / "Sends 8× (once per topic)" from the send count. */
function sendCadenceLabel(item: EmailTemplateListItem): string {
  if (item.send_count <= 1) return "Sends once";
  return `Sends ${item.send_count}× (once per topic)`;
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <p className="mt-1 text-sm text-gray-900">{value}</p>
    </div>
  );
}

function PreviewPane({ selectedId }: { selectedId: string | undefined }) {
  const detailQuery = useEmailTemplate(selectedId);

  if (!selectedId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-gray-500">
        Select a template to preview it
      </div>
    );
  }

  if (detailQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (detailQuery.isError) {
    const message =
      detailQuery.error instanceof Error ? detailQuery.error.message : "Failed to load template";
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-red-700">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {message}
        </div>
        <button
          type="button"
          onClick={() => detailQuery.refetch()}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-100"
        >
          Try again
        </button>
      </div>
    );
  }

  const template = detailQuery.data;

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{template.subject}</h2>
        <p className="mt-1 text-sm text-gray-500">{template.preview}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MetaRow label="Template ID" value={template.id} />
        <MetaRow label="Category" value={template.category} />
        <MetaRow label="Trigger" value={template.trigger} />
        <MetaRow label="Cadence" value={sendCadenceLabel(template)} />
        <div className="sm:col-span-2">
          <span className="text-xs font-medium text-gray-500">Variables</span>
          <p className="mt-1 flex flex-wrap gap-1.5">
            {template.variables.map((v) => (
              <code
                key={v}
                className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700"
              >{`{{ ${v} }}`}</code>
            ))}
          </p>
        </div>
      </div>

      <div className="flex-1">
        <span className="text-xs font-medium text-gray-500">Preview</span>
        <iframe
          title={`Preview of ${template.subject}`}
          srcDoc={template.rendered_html}
          className="mt-1 h-full min-h-[480px] w-full rounded-lg border border-gray-200 bg-white"
          sandbox=""
        />
      </div>
    </div>
  );
}

function TemplatesTab() {
  const listQuery = useEmailTemplates();
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const templates = listQuery.data;

  // Auto-select the first template once the list loads.
  useEffect(() => {
    if (!selectedId && templates && templates.length > 0) {
      setSelectedId(templates[0].id);
    }
  }, [selectedId, templates]);

  return (
    <div className="flex h-full flex-col gap-4 md:flex-row">
      {/* List pane — full width on mobile (capped, scrollable), fixed sidebar at md+ */}
      <div className="flex max-h-[40vh] w-full flex-shrink-0 flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white md:max-h-none md:w-80">
        {listQuery.isPending && (
          <div className="flex items-center justify-center p-8 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {listQuery.isError && (
          <div className="flex flex-col items-center gap-3 p-8 text-sm text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {listQuery.error instanceof Error
                ? listQuery.error.message
                : "Failed to load templates"}
            </div>
            <button
              type="button"
              onClick={() => listQuery.refetch()}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-gray-700 hover:bg-gray-100"
            >
              Try again
            </button>
          </div>
        )}

        {templates?.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-500">No email templates</div>
        )}

        {templates?.map((t) => {
          const active = t.id === selectedId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedId(t.id)}
              aria-current={active}
              className={`border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 ${
                active ? "bg-primary/5" : "hover:bg-gray-50"
              }`}
            >
              <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                {t.category}
              </span>
              <p className="mt-0.5 truncate text-sm font-medium text-gray-900">{t.subject}</p>
              <p className="mt-0.5 truncate text-xs text-gray-500">{t.trigger}</p>
            </button>
          );
        })}
      </div>

      {/* Preview pane */}
      <div className="min-h-0 flex-1 rounded-lg border border-gray-200 bg-white p-5">
        <PreviewPane selectedId={selectedId} />
      </div>
    </div>
  );
}

const TABS = [
  { key: "templates", label: "Templates" },
  { key: "send", label: "Send" },
  { key: "schedule", label: "Schedule" },
  { key: "log", label: "Log" },
  { key: "suppression", label: "Suppression" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function AdminEmailTemplatesPage() {
  const [tab, setTab] = useState<TabKey>("templates");

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        <div className="flex gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1">
          {tab === "templates" && <TemplatesTab />}
          {tab === "send" && <AdHocSendTab />}
          {tab === "schedule" && <ScheduleTab />}
          {tab === "log" && <LogTab />}
          {tab === "suppression" && <SuppressionTab />}
        </div>
      </div>
    </AdminLayout>
  );
}
